import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi";
import { useSelector, useDispatch } from "react-redux";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import APIs from "../../Constants/apis";
import dayjs from 'dayjs';
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import { setFilterField } from "../../app/itemGridPreferencesSlice";
import { floorLoyaltyPoints } from "../../utils/loyaltyPoints";
import "./analytics.css";

const getDefaultDateRange = () => [
  dayjs().subtract(30, "days").startOf("day").toDate(),
  dayjs().endOf("day").toDate(),
];

const parseStoredDateRange = (range) => {
  const fallback = getDefaultDateRange();
  if (!range || !Array.isArray(range) || range.length < 2) {
    return fallback;
  }
  const start = range[0] ? dayjs(range[0]).startOf("day").toDate() : fallback[0];
  const end = range[1] ? dayjs(range[1]).endOf("day").toDate() : fallback[1];
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return fallback;
  }
  return [start, end];
};

const formatRangeKey = (start, end) =>
  `${dayjs(start).format("YYYY-MM-DD")}|${dayjs(end).format("YYYY-MM-DD")}`;

const LoyaltyAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const dispatch = useDispatch();
  const pageKey = "loyaltyReport";
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const analyticsFetchSeqRef = useRef(0);
  const lastErrorToastKeyRef = useRef("");

  const dateRange = useMemo(
    () => parseStoredDateRange(preferences.dateRange),
    [preferences.dateRange]
  );

  const dateRangeKey = useMemo(
    () => formatRangeKey(dateRange[0], dateRange[1]),
    [dateRange]
  );

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const showFetchError = useCallback((message) => {
    const key = message || "Error loading loyalty analytics";
    if (lastErrorToastKeyRef.current === key) return;
    lastErrorToastKeyRef.current = key;
    showErrorToast(key);
  }, []);

  const loadAnalytics = useCallback(
    async (showSuccessMessage = false, overrideStartDate = null, overrideEndDate = null) => {
      if (!CompID || CompID <= 0) {
        showFetchError("Company ID is not available");
        return;
      }

      const startDateToUse = overrideStartDate || dateRange[0];
      const endDateToUse = overrideEndDate || dateRange[1];

      if (!startDateToUse || !endDateToUse) {
        setLoading(false);
        setInitialLoadComplete(true);
        return;
      }

      const startDate = dayjs(startDateToUse).format("YYYY-MM-DD");
      const endDate = dayjs(endDateToUse).format("YYYY-MM-DD");
      const fetchSeq = ++analyticsFetchSeqRef.current;

      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `${APIs.GET_LOYALTY_ANALYTICS}?compID=${CompID}&startDate=${startDate}&endDate=${endDate}`
        );

        if (fetchSeq !== analyticsFetchSeqRef.current) return;

        const isSuccess = response.data.Status || response.data.status;
        const responseData = response.data.Data || response.data.data;

        if (isSuccess && responseData) {
          lastErrorToastKeyRef.current = "";
          setAnalytics(responseData);
          if (showSuccessMessage) {
            showSuccessToast("Analytics loaded successfully");
          }
        } else {
          const message =
            response.data.Message ||
            response.data.message ||
            "Failed to load loyalty analytics";
          showFetchError(message);
        }
      } catch (error) {
        if (fetchSeq !== analyticsFetchSeqRef.current) return;
        const message =
          error.response?.data?.Message ||
          error.response?.data?.message ||
          "Error loading loyalty analytics";
        showFetchError(message);
      } finally {
        if (fetchSeq === analyticsFetchSeqRef.current) {
          setLoading(false);
          setInitialLoadComplete(true);
        }
      }
    },
    [CompID, dateRange, showFetchError]
  );


  const handleDateRangeChange = (update) => {
    if (!update || !Array.isArray(update)) {
      return;
    }

    const [newStartDate, newEndDate] = update;

    let finalStartDate;
    let finalEndDate;

    if (newStartDate && !newEndDate) {
      finalStartDate = dayjs(newStartDate).startOf("day").toDate();
      finalEndDate = dayjs(newStartDate).endOf("day").toDate();
    } else if (newStartDate && newEndDate) {
      finalStartDate = dayjs(newStartDate).startOf("day").toDate();
      finalEndDate = dayjs(newEndDate).endOf("day").toDate();
    } else {
      return;
    }

    const nextKey = formatRangeKey(finalStartDate, finalEndDate);
    if (nextKey === dateRangeKey) {
      return;
    }

    lastErrorToastKeyRef.current = "";
    dispatch(
      setFilterField({
        pageKey,
        field: "dateRange",
        value: [finalStartDate, finalEndDate],
      })
    );

  };

  useEffect(() => {
    if (!preferences.dateRange) {
      dispatch(
        setFilterField({ pageKey, field: "dateRange", value: getDefaultDateRange() })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useEffect(() => {
    if (!CompID || CompID <= 0 || !dateRangeKey) return;
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CompID, dateRangeKey]);





  if (!CompID || CompID <= 0) {
    return (
      <div>
        <div className="alert alert-warning">
          <i className="fa fa-exclamation-triangle me-2"></i>
          Company ID is not available. Please refresh the page.
        </div>
      </div>
    );
  }

  // Handle both camelCase and PascalCase property names
  const totalLoyaltyCustomers = analytics?.totalLoyaltyCustomers || analytics?.TotalLoyaltyCustomers || 0;
  const pointsEarnedInPeriod = analytics?.pointsEarnedInPeriod || analytics?.PointsEarnedInPeriod || 0;
  const pointsSpentInPeriod = analytics?.pointsSpentInPeriod || analytics?.PointsSpentInPeriod || 0;
  const totalPointsEarned = analytics?.totalPointsEarned || analytics?.TotalPointsEarned || 0;
  const totalPointsSpent = analytics?.totalPointsSpent || analytics?.TotalPointsSpent || 0;
  const totalPointsInCirculation = analytics?.totalPointsInCirculation || analytics?.TotalPointsInCirculation || 0;
  const totalTransactionsInPeriod = analytics?.totalTransactionsInPeriod || analytics?.TotalTransactionsInPeriod || 0;
  const customersAvailedInPeriod = analytics?.customersAvailedInPeriod || analytics?.CustomersAvailedInPeriod || 0;
  const popularRewards = analytics?.popularRewards || analytics?.PopularRewards || [];
  const topCustomers = analytics?.topCustomers || analytics?.TopCustomers || [];

  const redemptionRate = totalPointsEarned > 0 ?
    Math.round((totalPointsSpent / totalPointsEarned) * 100) : 0;
  const estimatedRoi =
    totalPointsEarned > 0
      ? Math.round((totalPointsSpent / totalPointsEarned) * 300)
      : 0;
  const maxRedemptionCount = popularRewards[0]?.redemptionCount || popularRewards[0]?.RedemptionCount || 1;
  const maxTransactionCount = topCustomers[0]?.transactionCount || topCustomers[0]?.TransactionCount || 1;

  const rangeLabel = `${dayjs(dateRange[0]).format("MMM D, YYYY")} – ${dayjs(dateRange[1]).format("MMM D, YYYY")}`;

  const themeStyle = {
    "--lr-accent": themeColor || "#30d6a4",
    "--lr-accent-soft": `${themeColor || "#30d6a4"}24`,
  };

  const renderEmpty = (icon, title, text) => (
    <div className="loyalty-report__empty">
      <div className="loyalty-report__empty-icon" aria-hidden="true">
        <i className={`fa ${icon}`} />
      </div>
      <h3 className="loyalty-report__empty-title">{title}</h3>
      <p className="loyalty-report__empty-text">{text}</p>
    </div>
  );

  return (
    <div
      className={`loyalty-report${isDarkMode ? " loyalty-report--dark" : ""}`}
      style={themeStyle}
    >
      {!initialLoadComplete && (
        <GridSectionLoader loading minHeight="400px" />
      )}

      {initialLoadComplete && (
        <GridSectionLoader loading={loading} minHeight="320px">
          <div className="loyalty-report__header">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item">
                <a href="#/report">Report</a>
              </li>
              <li className="breadcrumb-item active">Loyalty Report</li>
            </ol>
            <h1 className="page-header">
              <i className="fa fa-award me-2" aria-hidden="true" />
              Loyalty Report
            </h1>
            <p className="loyalty-report__subtitle">
              Program performance for <strong>{rangeLabel}</strong>
            </p>
          </div>

          <div className="loyalty-report__toolbar">
            <ModernGridToolbar
              isDarkMode={isDarkMode}
              title="Program activity"
              titleAddon={
                <span className="loyalty-report__badge loyalty-report__badge--neutral">
                  {rangeLabel}
                </span>
              }
              topFilters={
                <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                  <DateRangePicker
                    startDate={
                      dateRange[0] && dayjs(dateRange[0]).isValid()
                        ? dateRange[0]
                        : null
                    }
                    endDate={
                      dateRange[1] && dayjs(dateRange[1]).isValid()
                        ? dateRange[1]
                        : null
                    }
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                    allowSingleDate={true}
                    allowDateRange={true}
                  />
                </ModernGridToolbarFilterPill>
              }
            />
          </div>

          <div className="loyalty-report__metrics">
            <div className="loyalty-report__metric loyalty-report__metric--accent">
              <div className="loyalty-report__metric-icon" aria-hidden="true">
                <i className="fa fa-coins" />
              </div>
              <div className="loyalty-report__metric-body">
                <span className="loyalty-report__metric-label">
                  Unredeemed points
                </span>
                <span className="loyalty-report__metric-value loyalty-report__metric-value--accent">
                  {floorLoyaltyPoints(totalPointsInCirculation).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="loyalty-report__metric">
              <div
                className="loyalty-report__metric-icon loyalty-report__metric-icon--users"
                aria-hidden="true"
              >
                <i className="fa fa-users" />
              </div>
              <div className="loyalty-report__metric-body">
                <span className="loyalty-report__metric-label">
                  Loyalty customers
                </span>
                <span className="loyalty-report__metric-value">
                  {totalLoyaltyCustomers.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="loyalty-report__metric">
              <div
                className="loyalty-report__metric-icon loyalty-report__metric-icon--active"
                aria-hidden="true"
              >
                <i className="fa fa-user-check" />
              </div>
              <div className="loyalty-report__metric-body">
                <span className="loyalty-report__metric-label">
                  Active in period
                </span>
                <span className="loyalty-report__metric-value">
                  {customersAvailedInPeriod.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="loyalty-report__metric">
              <div
                className="loyalty-report__metric-icon loyalty-report__metric-icon--earned"
                aria-hidden="true"
              >
                <i className="fa fa-arrow-up" />
              </div>
              <div className="loyalty-report__metric-body">
                <span className="loyalty-report__metric-label">Points earned</span>
                <span className="loyalty-report__metric-value loyalty-report__metric-value--success">
                  {floorLoyaltyPoints(pointsEarnedInPeriod).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="loyalty-report__metric">
              <div
                className="loyalty-report__metric-icon loyalty-report__metric-icon--redeemed"
                aria-hidden="true"
              >
                <i className="fa fa-arrow-down" />
              </div>
              <div className="loyalty-report__metric-body">
                <span className="loyalty-report__metric-label">
                  Points redeemed
                </span>
                <span className="loyalty-report__metric-value loyalty-report__metric-value--danger">
                  {floorLoyaltyPoints(pointsSpentInPeriod).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="loyalty-report__panels">
            <section className="loyalty-report__panel">
              <div className="loyalty-report__panel-head">
                <h2 className="loyalty-report__panel-title">
                  <i className="fa fa-gift" aria-hidden="true" />
                  Top performing rewards
                </h2>
                <p className="loyalty-report__panel-sub">
                  Ranked by redemption count in the selected range
                </p>
              </div>
              <div className="loyalty-report__panel-body">
                {popularRewards && popularRewards.length > 0 ? (
                  <div className="table-responsive">
                    <table className="loyalty-report__table">
                      <thead>
                        <tr>
                          <th>Reward</th>
                          <th className="text-center">Points cost</th>
                          <th className="text-center">Redemptions</th>
                          <th className="text-center">Points spent</th>
                          <th>Popularity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {popularRewards.slice(0, 10).map((reward, index) => {
                          const rewardName =
                            reward.rewardName || reward.RewardName || "";
                          const rewardType =
                            reward.rewardType || reward.RewardType || "";
                          const requiredPoints =
                            reward.requiredPoints || reward.RequiredPoints || 0;
                          const redemptionCount =
                            reward.redemptionCount ||
                            reward.RedemptionCount ||
                            0;
                          const totalPtsSpent =
                            reward.totalPointsSpent ||
                            reward.TotalPointsSpent ||
                            0;
                          const popularityPercent = Math.round(
                            (redemptionCount / maxRedemptionCount) * 100
                          );
                          return (
                            <tr key={index}>
                              <td>
                                <div className="loyalty-report__name">
                                  {rewardName}
                                </div>
                                {rewardType ? (
                                  <div className="loyalty-report__meta">
                                    {rewardType}
                                  </div>
                                ) : null}
                              </td>
                              <td className="text-center">
                                {floorLoyaltyPoints(requiredPoints)} pts
                              </td>
                              <td className="text-center">
                                <span className="loyalty-report__badge">
                                  {redemptionCount}
                                </span>
                              </td>
                              <td className="text-center">
                                <strong>
                                  {floorLoyaltyPoints(totalPtsSpent)} pts
                                </strong>
                              </td>
                              <td>
                                <div
                                  className="loyalty-report__bar"
                                  title={`${popularityPercent}%`}
                                >
                                  <span
                                    style={{ width: `${popularityPercent}%` }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  renderEmpty(
                    "fa-gift",
                    "No rewards data yet",
                    "When customers redeem rewards in this period, top performers will appear here."
                  )
                )}
              </div>
            </section>

            <section className="loyalty-report__panel">
              <div className="loyalty-report__panel-head">
                <h2 className="loyalty-report__panel-title">
                  <i className="fa fa-user-friends" aria-hidden="true" />
                  Top 10 customers
                </h2>
                <p className="loyalty-report__panel-sub">
                  Based on transaction count in the selected range
                </p>
              </div>
              <div className="loyalty-report__panel-body">
                {topCustomers && topCustomers.length > 0 ? (
                  <div className="table-responsive">
                    <table className="loyalty-report__table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th className="text-center">Current points</th>
                          <th className="text-center">Txns</th>
                          <th className="text-center">Earned</th>
                          <th className="text-center">Spent</th>
                          <th>Activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCustomers.slice(0, 10).map((customer, index) => {
                          const customerName =
                            customer.customerName ||
                            customer.CustomerName ||
                            "Unknown";
                          const currentPoints =
                            customer.currentPoints ||
                            customer.CurrentPoints ||
                            0;
                          const transactionCount =
                            customer.transactionCount ||
                            customer.TransactionCount ||
                            0;
                          const pointsEarned =
                            customer.pointsEarnedInPeriod ||
                            customer.PointsEarnedInPeriod ||
                            0;
                          const pointsSpent =
                            customer.pointsSpentInPeriod ||
                            customer.PointsSpentInPeriod ||
                            0;
                          const activityPercent = Math.round(
                            (transactionCount / maxTransactionCount) * 100
                          );
                          return (
                            <tr key={index}>
                              <td>
                                <div className="loyalty-report__name">
                                  {customerName}
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="loyalty-report__badge loyalty-report__badge--info">
                                  {floorLoyaltyPoints(currentPoints)}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="loyalty-report__badge">
                                  {transactionCount}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="loyalty-report__delta--up">
                                  +{floorLoyaltyPoints(pointsEarned)}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="loyalty-report__delta--down">
                                  −{floorLoyaltyPoints(pointsSpent)}
                                </span>
                              </td>
                              <td>
                                <div
                                  className="loyalty-report__bar loyalty-report__bar--alt"
                                  title={`${activityPercent}%`}
                                >
                                  <span
                                    style={{ width: `${activityPercent}%` }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  renderEmpty(
                    "fa-users",
                    "No customer activity",
                    "No loyalty transactions in this date range. Try expanding the period with the date picker above."
                  )
                )}
              </div>
            </section>
          </div>

          <div className="loyalty-report__insights">
            <section className="loyalty-report__insight">
              <h2 className="loyalty-report__insight-title">
                <i className="fa fa-chart-line" aria-hidden="true" />
                Program ROI insights
              </h2>
              <div className="loyalty-report__roi">
                <div
                  className="loyalty-report__roi-ring"
                  style={{ "--lr-roi": Math.min(estimatedRoi, 100) }}
                  aria-label={`${estimatedRoi}% estimated program ROI`}
                >
                  <span className="loyalty-report__roi-ring-value">
                    {estimatedRoi}%
                  </span>
                </div>
                <div className="loyalty-report__roi-copy">
                  <p className="loyalty-report__roi-label">
                    Estimated program ROI
                  </p>
                  <p className="loyalty-report__roi-hint">
                    Based on points redeemed vs earned
                    {redemptionRate > 0
                      ? ` · ${redemptionRate}% redemption rate`
                      : ""}
                    .
                  </p>
                </div>
              </div>
              <div className="loyalty-report__stat-row">
                <span className="loyalty-report__stat-label">
                  Total points earned
                </span>
                <span className="loyalty-report__stat-value">
                  {floorLoyaltyPoints(totalPointsEarned).toLocaleString()}
                </span>
              </div>
              <div className="loyalty-report__stat-row">
                <span className="loyalty-report__stat-label">
                  Total points redeemed
                </span>
                <span className="loyalty-report__stat-value">
                  {floorLoyaltyPoints(totalPointsSpent).toLocaleString()}
                </span>
              </div>
              <div className="loyalty-report__stat-row">
                <span className="loyalty-report__stat-label">
                  Points in circulation
                </span>
                <span className="loyalty-report__stat-value">
                  {floorLoyaltyPoints(totalPointsInCirculation).toLocaleString()}
                </span>
              </div>
            </section>

            <section className="loyalty-report__insight">
              <h2 className="loyalty-report__insight-title">
                <i className="fa fa-lightbulb" aria-hidden="true" />
                Customer insights
              </h2>
              <div className="loyalty-report__stat-row">
                <span className="loyalty-report__stat-label">
                  Avg points per customer
                </span>
                <span className="loyalty-report__stat-value">
                  {Math.floor(
                    floorLoyaltyPoints(totalPointsInCirculation) /
                      Math.max(totalLoyaltyCustomers, 1)
                  ).toLocaleString()}
                </span>
              </div>
              <div className="loyalty-report__stat-row">
                <span className="loyalty-report__stat-label">Top reward</span>
                <span className="loyalty-report__stat-value">
                  {popularRewards[0]?.rewardName ||
                    popularRewards[0]?.RewardName ||
                    "N/A"}
                </span>
              </div>
              <div className="loyalty-report__stat-row">
                <span className="loyalty-report__stat-label">
                  Transaction volume
                </span>
                <span className="loyalty-report__stat-value">
                  {totalTransactionsInPeriod.toLocaleString()}
                </span>
              </div>
              <div className="loyalty-report__stat-row">
                <span className="loyalty-report__stat-label">
                  Active customers in range
                </span>
                <span className="loyalty-report__stat-value">
                  {customersAvailedInPeriod.toLocaleString()}
                </span>
              </div>
            </section>
          </div>
        </GridSectionLoader>
      )}
    </div>
  );
};

export default LoyaltyAnalytics;
